import { useState, useEffect, MouseEvent } from "react";
import { useMutation } from '@tanstack/react-query';
import { UseFormReturn } from "react-hook-form";
import { testDatastoreConnectionDuringCreation } from "@/helpers/apis";
import { handleConnectionTestToast } from "../utils";
import { TestDatastoreConnectionDuringCreationRequest } from "@/pages/datastores/types";
import { SnowflakeFormValues } from "../SnowflakeConnection";

interface ConnectionTestHookResult {
  connectionTestStatus: "untested" | "success" | "failed";
  isTestingConnection: boolean;
  handleTestConnection: (
    e: MouseEvent<HTMLButtonElement>,
    data: TestDatastoreConnectionDuringCreationRequest
  ) => void;
  testConnectionAndProceed: (
    data: TestDatastoreConnectionDuringCreationRequest,
    onSuccess: () => void
  ) => void;
}

export const useSnowflakeConnectionTest = (
  form: UseFormReturn<SnowflakeFormValues>
): ConnectionTestHookResult => {
  const [connectionTestStatus, setConnectionTestStatus] = useState<
    "untested" | "success" | "failed"
  >("untested");
  const [isConnectionTested, setIsConnectionTested] = useState(false);

  // Track form changes to reset connection test status when form becomes dirty
  useEffect(() => {
    if (connectionTestStatus === "failed" && form.formState.isDirty && isConnectionTested) {
      setConnectionTestStatus("untested");
      setIsConnectionTested(false);
    }
  }, [form.formState.isDirty, connectionTestStatus, isConnectionTested]);

  const handleConnectionTestResponse = (
    response?: {
      ok?: boolean;
      message?: string;
    },
    onSuccessCallback?: () => void
  ) => {
    if (response?.ok) {
      setConnectionTestStatus("success");
      setIsConnectionTested(true);
      form.reset(form.getValues(), { keepValues: true });
      handleConnectionTestToast(response);
      onSuccessCallback?.();
    } else {
      setConnectionTestStatus("failed");
      setIsConnectionTested(true);
      form.reset(form.getValues(), { keepValues: true });
      handleConnectionTestToast(response);
    }
  };

  const handleConnectionTestError = () => {
    setConnectionTestStatus("failed");
    setIsConnectionTested(true);
    form.reset(form.getValues(), { keepValues: true });
    handleConnectionTestToast();
  };

  const { mutate: testDatastoreConnection, isPending: isTestingConnection } = useMutation({
    mutationFn: (data: Partial<TestDatastoreConnectionDuringCreationRequest>) =>
      testDatastoreConnectionDuringCreation(data),
    onSuccess: response => handleConnectionTestResponse(response),
    onError: handleConnectionTestError,
  });

  const handleTestConnection = (
    e: MouseEvent<HTMLButtonElement>,
    data: TestDatastoreConnectionDuringCreationRequest
  ) => {
    e.preventDefault();
    setConnectionTestStatus("untested");
    testDatastoreConnection(data);
  };

  const testConnectionAndProceed = (
    data: TestDatastoreConnectionDuringCreationRequest,
    onSuccess: () => void
  ) => {
    setConnectionTestStatus("untested");
    testDatastoreConnection(data, {
      onSuccess: response => {
        handleConnectionTestResponse(response, onSuccess);
      },
      onError: handleConnectionTestError,
    });
  };

  return {
    connectionTestStatus,
    isTestingConnection,
    handleTestConnection,
    testConnectionAndProceed,
  };
};
